import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

// In-memory cache per page so route changes don't re-fetch every time.
const _cache = new Map();

/**
 * useContent — fetches the published CMS content for a page.
 *
 * Returns:
 *   {
 *     content,      // raw published snapshot (may be empty {})
 *     ready,        // false until the fetch resolves the first time
 *     get(key, fallback)  // helper: read nested keys with a static fallback
 *   }
 *
 * Nested keys use dot notation, e.g. get("hero.headline", "DEFAULT").
 *
 * The site MUST keep working when the CMS is empty — every caller
 * supplies a static fallback (typically the existing hard-coded value).
 */
export default function useContent(page) {
  const cached = _cache.get(page);
  const [content, setContent] = useState(cached || {});
  const [ready, setReady] = useState(!!cached);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/content/${page}`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((data) => {
        if (cancelled) return;
        _cache.set(page, data || {});
        setContent(data || {});
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [page]);

  function get(key, fallback) {
    if (!key) return fallback;
    const parts = key.split(".");
    let node = content;
    for (const p of parts) {
      if (node && typeof node === "object" && p in node) {
        node = node[p];
      } else {
        return fallback;
      }
    }
    // Treat empty string as "use fallback" so the editor cannot accidentally
    // erase a section by clearing the field.
    if (node === undefined || node === null || node === "") return fallback;
    return node;
  }

  return { content, ready, get };
}
